/**
 * member-deep-link.ts: the `?member=<id>` link on the Lookup (row 418, 19 Sep 2026).
 *
 * LIFTED from the Portal Frontend, v1/src/lib/member-deep-link.ts (its row 402, approval 197), with
 * attribution and one change of prose: the link here lands on Care's own Lookup,
 *
 *   https://membercare.claridynamics.com/lookup?member=<id>
 *
 * and opens that member's card on the Member tab after sign-in and the reason gate, against the
 * client's roster. This module is the pure half: read the id, find it on the loaded roster, and
 * strip the parameter so a refresh does not re-open the card. Two older internal links carry
 * `?member_id=`; both spellings are read. Nothing here loads a row; the page resolves the id only
 * after the roster it is allowed to see has loaded, and audits the open as a search that arrived by
 * link.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */

const PARAM_NAMES = ['member', 'member_id'] as const;
/** Roster ids are 5 to 9 characters of digits today; a link value outside a short, plain shape is ignored. */
const ID_SHAPE = /^[A-Za-z0-9-]{1,20}$/;

/** The member id a link carries, or null when the query string has none worth acting on. */
export function readMemberDeepLink(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const name of PARAM_NAMES) {
    const raw = params.get(name);
    if (raw === null) continue;
    const v = raw.trim();
    if (v && ID_SHAPE.test(v)) return v;
  }
  return null;
}

/** The same query string with the member parameters removed ('' when nothing is left). */
export function stripMemberDeepLink(search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const name of PARAM_NAMES) params.delete(name);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/**
 * The roster's own id for a linked member: an exact match first, then a match that ignores
 * leading zeros (a link typed by hand may drop them; the roster never does). Null when the member
 * is not on this roster.
 */
export function findRosterMember(rows: ReadonlyArray<{ member_id: string }>, id: string): string | null {
  const exact = rows.find((r) => r.member_id === id);
  if (exact) return exact.member_id;
  const bare = id.replace(/^0+/, '');
  if (!bare) return null;
  const loose = rows.find((r) => r.member_id.replace(/^0+/, '') === bare);
  return loose ? loose.member_id : null;
}
